import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { CreditCard, Loader2, ArrowLeft, MapPin, Clock, Shield } from "lucide-react";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";

export default function Checkout() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then(r => { setBooking(r.data); setLoading(false); })
      .catch(() => { toast.error("Booking not found"); setLoading(false); navigate("/bookings"); });
  }, [bookingId, navigate]);

  const handlePay = async () => {
    if (user && !hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    setPaying(true);
    try {
      const originUrl = window.location.origin;
      const res = await api.post("/payments/checkout", { booking_id: bookingId, origin_url: originUrl });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
      setPaying(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!booking) return null;

  const platformFee = booking.platform_fee || (booking.total_amount * 0.05);
  const kycBlocked = user && !hasApprovedBookingKyc(user);
  const kycMessage = getBookingKycMessage(user);

  return (
    <div className="max-w-lg mx-auto px-4 md:px-8 py-8">
      <button data-testid="checkout-back-btn" onClick={() => navigate("/bookings")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" />Back to bookings
      </button>

      <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-6" data-testid="checkout-title">Checkout</h1>

      <div className="bg-white border-2 border-slate-200 p-6 mb-6">
        <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-4">Booking Summary</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Venue</span><span className="font-medium">{booking.venue_name}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Court</span><span className="font-medium">{booking.resource_name}</span></div>
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />Time</span>
            <span className="font-medium">{booking.start_dt?.slice(11, 16)} - {booking.end_dt?.slice(11, 16)}</span>
          </div>
          <div className="flex justify-between"><span className="text-muted-foreground">Date</span><span className="font-medium">{booking.start_dt?.slice(0, 10)}</span></div>
          <div className="flex justify-between capitalize"><span className="text-muted-foreground">Sport</span><span className="font-medium">{booking.sport_type}</span></div>
          <Separator />
          <div className="flex justify-between"><span className="text-muted-foreground">Court Fee</span><span>₱{(booking.total_amount - platformFee).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Service Fee</span><span>₱{platformFee.toFixed(2)}</span></div>
          <Separator />
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span><span className="text-court-orange">₱{booking.total_amount?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {booking.status === "PENDING_PAYMENT" ? (
        <div className="space-y-4">
          {kycBlocked && <KycBookingNotice message={kycMessage} compact testId="checkout-kyc-notice" />}
          <Button data-testid="pay-stripe-btn" onClick={handlePay} disabled={paying || kycBlocked}
            className="w-full bg-court-orange hover:bg-court-orange-hover text-white rounded-full font-heading uppercase tracking-wide h-14 text-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:shadow-none">
            {paying ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CreditCard className="w-5 h-5 mr-2" />}
            {paying ? "Redirecting to Stripe..." : `Pay ₱${booking.total_amount?.toFixed(2)}`}
          </Button>
          <div className="flex items-center gap-2 justify-center text-xs text-muted-foreground">
            <Shield className="w-3 h-3" />Secure payment powered by Stripe
          </div>
        </div>
      ) : (
        <div className="text-center p-6 bg-slate-50 border-2 border-slate-200">
          <p className="font-heading text-lg uppercase">Booking Status: {booking.status}</p>
          <Button data-testid="go-bookings-btn" onClick={() => navigate("/bookings")} className="mt-4 rounded-full">View My Bookings</Button>
        </div>
      )}
    </div>
  );
}
